import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SystemIssue, SystemIssueSource } from "@backstory/protocol";
import { AiOutput } from "../ai/components/common";
import { Dialog, Field, useToast } from "../../components/ui";
import { formatDateTime, formatRelative } from "../../lib/format";
import { staffApi } from "./api";

export const sourceLabels: Record<SystemIssueSource, string> = {
  background_jobs: "Background job",
  ai: "AI",
  deep_analysis: "AI analysis",
  ai_health: "AI route health",
  exports: "Session export",
  warehouse: "Warehouse",
  notifications: "Notification",
  assets: "Asset archival",
  billing: "Billing",
};

/**
 * The three decisions staff can make about a grouped failure. Every one of them
 * changes both the queue and the drill-in, so the invalidation lives here rather
 * than being repeated, and half-remembered, at each call site.
 */
export function useSystemIssueActions(id: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["staff", "system-issues"] });
    void qc.invalidateQueries({ queryKey: ["staff", "system-issue", id] });
  };
  return {
    investigate: useMutation({ mutationFn: () => staffApi.investigateSystemIssue(id), onSuccess: refresh }),
    acknowledge: useMutation({ mutationFn: (note: string) => staffApi.acknowledgeSystemIssue(id, note), onSuccess: refresh }),
    reopen: useMutation({ mutationFn: () => staffApi.reopenSystemIssue(id), onSuccess: refresh }),
  };
}

/**
 * Acknowledging empties a row out of the queue, which reads like deleting or
 * fixing it. It is neither, and the difference matters when the thing is still
 * failing in production, so the dialog states what actually happens before the
 * row disappears. The note is optional and goes to the audit log, which is the
 * only place the next operator can learn why this was waved through.
 */
export function AcknowledgeDialog({ issue, open, onClose }: { issue: SystemIssue; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState("");
  const { acknowledge } = useSystemIssueActions(issue.id);
  const close = () => {
    setNote("");
    acknowledge.reset();
    onClose();
  };
  const failures = `${issue.occurrences.toLocaleString()} ${issue.occurrences === 1 ? "failure" : "failures"}`;
  return (
    <Dialog
      open={open}
      onClose={close}
      title="Acknowledge this issue?"
      width={480}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={close} disabled={acknowledge.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={acknowledge.isPending}
            onClick={() =>
              acknowledge.mutate(note.trim(), {
                onSuccess: () => {
                  toast.push("success", "Acknowledged. It will return if it happens again.");
                  close();
                },
              })
            }
          >
            {acknowledge.isPending ? "Acknowledging…" : "Acknowledge"}
          </button>
        </>
      }
    >
      <p>
        <strong>{issue.title}</strong>
        <span className="muted small" style={{ display: "block", marginTop: 2 }}>
          {sourceLabels[issue.source]} · {failures} · last seen {formatRelative(issue.lastSeen)}
        </span>
      </p>
      <p>Nothing is deleted and nothing is fixed. The {failures} recorded so far are marked as handled, so:</p>
      <ul className="sys-ack-effects">
        <li>it leaves the open queue and the dashboard count</li>
        <li>it stays readable under the Acknowledged filter, with your name against it</li>
        <li>it returns to the queue by itself if it fails again after now</li>
      </ul>
      <Field label="Note" hint="Optional. Recorded in the audit log for whoever sees this next.">
        {(p) => (
          <input
            {...p}
            className="input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={200}
            placeholder="Known upstream outage, ticket PLAT-412…"
          />
        )}
      </Field>
      {acknowledge.isError && (
        <div className="error-box" role="alert">
          {(acknowledge.error as Error).message}
        </div>
      )}
    </Dialog>
  );
}

/** Which customers a group reached, as one line. */
export function tenantLabel(issue: SystemIssue): string {
  if (issue.orgCount > 1) return `${issue.orgCount} organizations`;
  return issue.orgName || issue.orgId || "Platform";
}

/** Which customers a group reached. One name when it is one; a count when not. */
export function IssueScope({ issue }: { issue: SystemIssue }) {
  if (issue.orgCount > 1) return <span>{issue.orgCount} organizations</span>;
  if (!issue.orgName && !issue.orgId) return <span className="muted">Platform</span>;
  return (
    <>
      <span>{issue.orgName || issue.orgId}</span>
      {issue.projectName && <span className="muted small" style={{ display: "block" }}>{issue.projectName}</span>}
    </>
  );
}

export function IssueEnvironment({ issue }: { issue: SystemIssue }) {
  if (issue.environmentCount > 1) return <span className="small">{issue.environmentCount} environments</span>;
  if (!issue.environment) return <span className="muted">—</span>;
  return <span className="badge neutral">{issue.environment}</span>;
}

export function SeverityBadge({ severity }: { severity: SystemIssue["severity"] }) {
  return <span className={`badge ${severity === "high" ? "danger" : "warn"}`}>{severity}</span>;
}

/**
 * The saved answer for one group. It is rendered in the shared AI frame and laid
 * out in the order an operator acts on it: what happened, why, then what to do.
 */
export function InvestigationPanel({ issue, pending }: { issue: SystemIssue; pending: boolean }) {
  const saved = issue.investigation;
  const result = saved?.result;
  if (!result) return null;
  return (
    <AiOutput className="sys-investigation" testId={`system-issue-investigation-${issue.id}`}>
      <p>{result.summary}</p>
      <dl>
        <dt>Likely cause</dt>
        <dd>{result.likelyCause}</dd>
        <dt>Impact</dt>
        <dd>{result.impact}</dd>
        <dt>Confidence</dt>
        <dd>
          <Confidence value={result.confidence} />
        </dd>
      </dl>
      {result.recommendedActions.length > 0 && (
        <section>
          <h4>Recommended actions</h4>
          <ol>
            {result.recommendedActions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ol>
        </section>
      )}
      {result.evidence.length > 0 && (
        <details>
          <summary className="small">Evidence used</summary>
          <ul style={{ marginTop: 8 }}>
            {result.evidence.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        </details>
      )}
      {pending && <p className="muted small">A new investigation is running. This is the previous saved answer.</p>}
      {saved?.meta && <div className="ai-meta">Generated {formatDateTime(saved.meta.generatedAt)}</div>}
    </AiOutput>
  );
}

/** The model's self-reported certainty, shown as a bar so a weak answer is
 *  obvious at a glance rather than hidden in a number an operator skims past. */
function Confidence({ value }: { value: number }) {
  const pct = Math.round(Math.min(Math.max(value, 0), 1) * 100);
  const tone = pct >= 70 ? "ok" : pct >= 40 ? "warn" : "danger";
  return (
    <span className="sys-confidence">
      <span className="meter-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Model confidence">
        <span className={`fill ${tone}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="value">{pct}%</span>
    </span>
  );
}
